import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Plus, Trash2, Check, Loader2, Pencil, Eye, Palette, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  name: '',
  company_name: 'AEGIS',
  tagline: 'DÉVELOPPEMENT',
  primary_color: '#002FA7',
  signature_name: "L'équipe Aegis Développement",
  footer_text: 'Aegis Développement — Plateforme de gestion SEO',
};

export default function EmailBranding() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; object = edit
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [activating, setActivating] = useState('');
  const [testProfile, setTestProfile] = useState(null); // profile object for test dialog
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchProfiles = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/branding`, { withCredentials: true });
      setProfiles(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfiles(); }, []);

  const loadPreview = useCallback(async (data) => {
    try {
      const { data: res } = await axios.post(`${API}/settings/branding/preview`, data, { withCredentials: true });
      setPreviewHtml(res.html);
    } catch (e) {
      console.warn('preview failed', e);
    }
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      loadPreview(form);
    }
  }, [form, dialogOpen, loadPreview]);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      company_name: p.company_name || BLANK.company_name,
      tagline: p.tagline || BLANK.tagline,
      primary_color: p.primary_color || BLANK.primary_color,
      signature_name: p.signature_name || BLANK.signature_name,
      footer_text: p.footer_text || BLANK.footer_text,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom du profil est requis');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/settings/branding/${editing.id}`, form, { withCredentials: true });
        toast.success('Profil mis à jour');
      } else {
        await axios.post(`${API}/settings/branding`, form, { withCredentials: true });
        toast.success('Profil créé');
      }
      setDialogOpen(false);
      fetchProfiles();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    setActivating(id);
    try {
      await axios.post(`${API}/settings/branding/${id}/activate`, {}, { withCredentials: true });
      toast.success('Profil activé — utilisé pour les prochains emails');
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setActivating('');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le profil "${p.name}" ?`)) return;
    try {
      await axios.delete(`${API}/settings/branding/${p.id}`, { withCredentials: true });
      toast.success('Profil supprimé');
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testEmail.trim()) return;
    setSendingTest(true);
    try {
      const { data } = await axios.post(
        `${API}/settings/branding/${testProfile.id}/send-test`,
        { email: testEmail.trim() },
        { withCredentials: true }
      );
      toast.success(data.message);
      setTestProfile(null);
      setTestEmail('');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Layout>
      <div className="space-y-6" data-testid="email-branding-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Branding emails</h1>
            <p className="text-neutral-500 mt-1">Personnalisez le logo, les couleurs et la signature de vos emails sortants (invitations, mot de passe oublié, rapports).</p>
          </div>
          <Button onClick={openCreate} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="create-profile-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau profil
          </Button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#002FA7]" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="p-12 text-center border border-dashed border-neutral-200 rounded-lg">
            <Palette className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="text-neutral-500">Aucun profil encore. Créez-en un pour commencer.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map(p => (
              <div key={p.id}
                   className={`bg-white border rounded-lg overflow-hidden ${p.is_default ? 'border-[#002FA7] ring-2 ring-[#002FA7]/20' : 'border-neutral-200'}`}
                   data-testid={`profile-card-${p.id}`}>
                <div style={{ background: p.primary_color }} className="px-5 py-6">
                  <div className="inline-block border-2 border-white px-3 py-1.5 rounded">
                    <div className="text-white font-bold text-lg tracking-widest leading-none">{p.company_name}</div>
                    <div className="text-white/70 text-[9px] tracking-[0.4em] mt-0.5">{p.tagline}</div>
                  </div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-neutral-900">{p.name}</h3>
                    {p.is_default && (
                      <span className="text-xs px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] rounded-full font-medium">Actif</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 truncate">Signé : {p.signature_name}</p>
                  <div className="flex items-center gap-2 pt-2">
                    {!p.is_default && (
                      <Button size="sm" variant="outline" className="flex-1 text-xs"
                              onClick={() => activate(p.id)} disabled={activating === p.id}
                              data-testid={`activate-${p.id}`}>
                        {activating === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                        Activer
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setTestProfile(p); setTestEmail(''); }}
                            title="Envoyer un email de test" data-testid={`test-${p.id}`}>
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                    {!p.is_default && (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                              onClick={() => remove(p)} data-testid={`delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? `Modifier "${editing.name}"` : 'Nouveau profil de branding'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nom du profil <span className="text-red-500">*</span></Label>
                  <Input id="name" value={form.name} onChange={e => update('name', e.target.value)}
                         placeholder="Ex: Aegis Standard, Aegis Premium, Agence Partenaire X..."
                         data-testid="input-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="company_name">Logo — ligne principale</Label>
                    <Input id="company_name" value={form.company_name}
                           onChange={e => update('company_name', e.target.value)}
                           data-testid="input-company" />
                  </div>
                  <div>
                    <Label htmlFor="tagline">Logo — sous-titre</Label>
                    <Input id="tagline" value={form.tagline}
                           onChange={e => update('tagline', e.target.value)}
                           data-testid="input-tagline" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="primary_color">Couleur principale</Label>
                  <div className="flex gap-2 items-center">
                    <input type="color" id="primary_color" value={form.primary_color}
                           onChange={e => update('primary_color', e.target.value)}
                           className="w-12 h-10 rounded border border-neutral-200 cursor-pointer"
                           data-testid="input-color" />
                    <Input value={form.primary_color} onChange={e => update('primary_color', e.target.value)}
                           className="flex-1 font-mono" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="signature_name">Signature</Label>
                  <Input id="signature_name" value={form.signature_name}
                         onChange={e => update('signature_name', e.target.value)}
                         placeholder="L'équipe Aegis Développement"
                         data-testid="input-signature" />
                </div>
                <div>
                  <Label htmlFor="footer_text">Texte de pied de page</Label>
                  <Input id="footer_text" value={form.footer_text}
                         onChange={e => update('footer_text', e.target.value)}
                         data-testid="input-footer" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    {editing ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Eye className="w-4 h-4" /> Aperçu en direct
                </div>
                <div className="border border-neutral-200 rounded-lg overflow-hidden bg-neutral-100 p-3">
                  {previewHtml ? (
                    <iframe
                      title="Email preview"
                      srcDoc={previewHtml}
                      className="w-full bg-white"
                      style={{ height: 580, border: 0 }}
                      data-testid="preview-iframe"
                    />
                  ) : (
                    <div className="text-center text-sm text-neutral-400 py-12">Génération de l'aperçu...</div>
                  )}
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={!!testProfile} onOpenChange={(o) => { if (!o) setTestProfile(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Envoyer un email de test</DialogTitle>
            </DialogHeader>
            <form onSubmit={sendTest} className="space-y-4 mt-2">
              <p className="text-sm text-neutral-600">
                Envoie un exemple d'invitation avec le branding <strong>{testProfile?.name}</strong> à l'adresse indiquée.
              </p>
              <div>
                <Label htmlFor="test-email">Email destinataire</Label>
                <Input id="test-email" type="email" value={testEmail}
                       onChange={e => setTestEmail(e.target.value)}
                       placeholder="vous@example.com"
                       data-testid="test-email-input" required />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setTestProfile(null)}>Annuler</Button>
                <Button type="submit" disabled={sendingTest || !testEmail.trim()}
                        className="bg-[#002FA7] hover:bg-[#002277]"
                        data-testid="send-test-btn">
                  {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Envoyer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
